"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { StoreVideoSliderSection } from "../../types"
import styles from "./product-video-slider.module.css"

const VIDEO_VISIBILITY_THRESHOLD = 0.35
const OBSERVER_THRESHOLDS = [0, VIDEO_VISIBILITY_THRESHOLD, 0.75]

const cx = (...values: Array<string | false | null | undefined>) => values.filter(Boolean).join(" ")
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

const releaseBuffer = (video?: HTMLVideoElement | null) => {
  if (video?.getAttribute("src")) {
    video.removeAttribute("src")
    video.load()
  }
}

const Chevron = ({ direction }: { direction: "left" | "right" }) => (
  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d={direction === "left" ? "m15 18-6-6 6-6" : "m9 18 6-6-6-6"} />
  </svg>
)
const Play = () => <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="m7 4 13 8-13 8z" /></svg>
const Pause = () => <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M7 5h4v14H7zM13 5h4v14h-4z" /></svg>

type VideoItem = { id: string; title: string; posterSrc: string; videoSrc: string; alt: string }

const normalizeVideos = (section: StoreVideoSliderSection): VideoItem[] =>
  [...section.content.items]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((item, index) => {
      const posterSrc = item.poster_media?.url ?? item.video_media.poster_url ?? ""
      const videoSrc = item.video_media.url ?? ""
      return item.id && posterSrc && videoSrc
        ? {
            id: item.id,
            title: "",
            posterSrc,
            videoSrc,
            alt: item.poster_media?.alt_text ?? item.video_media.alt_text ?? item.accessible_label ?? `Customer pet video thumbnail ${index + 1}`,
          }
        : null
    })
    .filter((item): item is VideoItem => Boolean(item))

export function ProductVideoSlider({ section }: { section: StoreVideoSliderSection }) {
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const videoRefs = useRef(new Map<string, HTMLVideoElement>())
  const activeRef = useRef<string | null>(null)
  const rafRef = useRef<number | null>(null)
  const dragRef = useRef({ isDragging: false, pointerId: null as number | null, startX: 0, scrollLeft: 0 })
  const [activeId, setActiveId] = useState<string | null>(null)
  const [playingId, setPlayingId] = useState<string | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [failedPosters, setFailedPosters] = useState(() => new Set<string>())
  const videos = useMemo(() => normalizeVideos(section), [section])

  const registerVideo = useCallback((id: string, node: HTMLVideoElement | null) => {
    if (node) videoRefs.current.set(id, node)
    else videoRefs.current.delete(id)
  }, [])

  const deactivate = useCallback((id: string | null, options: { deactivate?: boolean; releaseBuffer?: boolean } = {}) => {
    if (!id) return
    const video = videoRefs.current.get(id)
    if (video) {
      if (!video.paused) video.pause()
      if (options.releaseBuffer) releaseBuffer(video)
    }
    if (options.deactivate) {
      activeRef.current = null
      setActiveId((current) => current === id ? null : current)
    }
    setPlayingId((current) => current === id ? null : current)
  }, [])

  const stopCurrent = useCallback(() => deactivate(activeRef.current, { releaseBuffer: true }), [deactivate])

  const playVideo = useCallback((item: VideoItem, index: number) => {
    if (activeRef.current && activeRef.current !== item.id) stopCurrent()
    activeRef.current = item.id
    setActiveId(item.id)
    setActiveIndex(index)
    const video = videoRefs.current.get(item.id)
    if (!video) return
    if (video.getAttribute("src") !== item.videoSrc) {
      video.setAttribute("src", item.videoSrc)
      video.load()
    }
    const playResult = video.play()
    if (playResult && typeof playResult.then === "function") {
      playResult.then(() => setPlayingId(item.id)).catch(() => setPlayingId(null))
    } else {
      setPlayingId(item.id)
    }
  }, [stopCurrent])

  const scrollToIndex = useCallback((index: number) => {
    const viewport = viewportRef.current
    if (!viewport || !videos.length) return
    const next = Math.min(Math.max(index, 0), videos.length - 1)
    const card = viewport.querySelector<HTMLElement>(`[data-product-video-index="${next}"]`)
    if (!card) return
    viewport.scrollTo({ left: card.offsetLeft, behavior: prefersReducedMotion() ? "auto" : "smooth" })
    setActiveIndex(next)
  }, [videos.length])

  useEffect(() => { activeRef.current = activeId }, [activeId])

  useEffect(() => () => {
    if (rafRef.current && typeof window !== "undefined") window.cancelAnimationFrame(rafRef.current)
    videoRefs.current.forEach((video) => {
      if (!video.paused) video.pause()
      releaseBuffer(video)
    })
  }, [])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport || typeof window === "undefined") return
    const updateIndex = () => {
      rafRef.current = null
      const cards = Array.from(viewport.querySelectorAll<HTMLElement>("[data-product-video-index]"))
      if (!cards.length) return
      const nearest = cards.reduce(
        (best, card) => {
          const index = Number(card.dataset.productVideoIndex)
          const distance = Math.abs(card.offsetLeft - viewport.scrollLeft)
          return distance < best.distance ? { index, distance } : best
        },
        { index: 0, distance: Number.POSITIVE_INFINITY }
      ).index
      setActiveIndex(nearest)
    }
    const onScroll = () => {
      if (!rafRef.current) rafRef.current = window.requestAnimationFrame(updateIndex)
    }
    viewport.addEventListener("scroll", onScroll, { passive: true })
    updateIndex()
    return () => {
      viewport.removeEventListener("scroll", onScroll)
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [videos.length])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport || typeof window === "undefined") return
    const stopDragging = () => {
      if (!dragRef.current.isDragging) return
      dragRef.current = { isDragging: false, pointerId: null, startX: 0, scrollLeft: 0 }
      setDragging(false)
    }
    const pointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null
      if (event.pointerType !== "mouse" || event.button !== 0 || target?.closest("button, a, video")) return
      dragRef.current = { isDragging: true, pointerId: event.pointerId, startX: event.clientX, scrollLeft: viewport.scrollLeft }
      viewport.setPointerCapture?.(event.pointerId)
      setDragging(true)
    }
    const pointerMove = (event: PointerEvent) => {
      const drag = dragRef.current
      if (!drag.isDragging || drag.pointerId !== event.pointerId) return
      event.preventDefault()
      viewport.scrollLeft = drag.scrollLeft - (event.clientX - drag.startX)
    }
    const pointerEnd = (event: PointerEvent) => {
      if (dragRef.current.pointerId === event.pointerId) viewport.releasePointerCapture?.(event.pointerId)
      stopDragging()
    }
    viewport.addEventListener("pointerdown", pointerDown)
    viewport.addEventListener("pointermove", pointerMove)
    viewport.addEventListener("pointerup", pointerEnd)
    viewport.addEventListener("pointercancel", pointerEnd)
    viewport.addEventListener("mouseleave", stopDragging)
    return () => {
      viewport.removeEventListener("pointerdown", pointerDown)
      viewport.removeEventListener("pointermove", pointerMove)
      viewport.removeEventListener("pointerup", pointerEnd)
      viewport.removeEventListener("pointercancel", pointerEnd)
      viewport.removeEventListener("mouseleave", stopDragging)
    }
  }, [])

  useEffect(() => {
    if (!activeId || typeof IntersectionObserver === "undefined" || typeof document === "undefined") return
    const card = viewportRef.current?.querySelector(`[data-product-video-id="${activeId}"]`)
    if (!card) return
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting || entry.intersectionRatio < VIDEO_VISIBILITY_THRESHOLD) {
        deactivate(activeId, { deactivate: true, releaseBuffer: true })
      }
    }, { threshold: OBSERVER_THRESHOLDS })
    observer.observe(card)
    return () => observer.disconnect()
  }, [activeId, deactivate])

  if (!videos.length) return null
  const hasPrevious = activeIndex > 0
  const hasNext = activeIndex < videos.length - 1
  const title = section.content.title?.trim() ?? ""

  return (
    <section className={styles.section} aria-label={title ? undefined : "Product videos"} aria-labelledby={title ? "product-video-slider-title" : undefined}>
      <div className={styles.header}>
        {title ? <h2 className={styles.title} id="product-video-slider-title">{title}</h2> : null}
        {videos.length > 1 ? <div className={styles.navControls} role="group" aria-label="Video navigation">
          <button className={styles.navButton} type="button" onClick={() => scrollToIndex(activeIndex - 1)} disabled={!hasPrevious} aria-label="Previous customer video"><Chevron direction="left" /></button>
          <button className={styles.navButton} type="button" onClick={() => scrollToIndex(activeIndex + 1)} disabled={!hasNext} aria-label="Next customer video"><Chevron direction="right" /></button>
        </div> : null}
      </div>
      <div className={cx(styles.viewport, dragging && styles.viewportDragging)} ref={viewportRef} data-testid="product-video-viewport">
        <div className={styles.track}>
          {videos.map((item, index) => {
            const playing = playingId === item.id
            const failedPoster = failedPosters.has(item.id)
            return <article key={item.id} className={styles.card} data-product-video-id={item.id} data-product-video-index={index}>
              <div className={styles.media}>
                {failedPoster ? <div className={styles.posterFallback} aria-hidden="true"><span className={styles.posterFallbackLabel}>Fresh Proof</span></div> : <img className={styles.poster} src={item.posterSrc} alt={item.alt} loading="lazy" decoding="async" onError={() => setFailedPosters((current) => new Set(current).add(item.id))} />}
                <video ref={(node) => registerVideo(item.id, node)} data-testid={`product-main-video-${item.id}`} className={cx(styles.video, activeId === item.id && styles.videoActive)} preload="none" playsInline muted={false} onPause={() => setPlayingId((current) => current === item.id ? null : current)} onEnded={() => setPlayingId((current) => current === item.id ? null : current)} />
                <div className={styles.mediaOverlay} aria-hidden="true" />
                {playing ? <button className={cx(styles.playButton, styles.pauseButton)} type="button" onClick={() => deactivate(item.id)} aria-label={`Pause customer video ${index + 1}`}><Pause /></button> : <button className={styles.playButton} type="button" onClick={() => playVideo(item, index)} aria-label={`Play customer video ${index + 1}`}><Play /></button>}
              </div>
              {item.title ? <p className={styles.caption}>{item.title}</p> : null}
            </article>
          })}
        </div>
      </div>
      {videos.length > 1 ? <div className={styles.dots} role="group" aria-label="Customer video pagination">{videos.map((item, index) => <button key={item.id} className={cx(styles.dot, activeIndex === index && styles.dotActive)} type="button" onClick={() => scrollToIndex(index)} aria-label={`Go to customer video ${index + 1}`} aria-current={activeIndex === index ? "true" : undefined} />)}</div> : null}
    </section>
  )
}

export default function LazyProductVideoSlider({ section }: { section: StoreVideoSliderSection }) {
  const [ready, setReady] = useState(false)
  const videoCount = section.content.items.length
  useEffect(() => {
    setReady(false)
    if (!videoCount || typeof window === "undefined") return
    let idleId: number | null = null
    let timeoutId: number | null = null
    const mount = () => setReady(true)
    const schedule = () => {
      if (typeof window.requestIdleCallback === "function") idleId = window.requestIdleCallback(mount, { timeout: 2500 })
      else timeoutId = window.setTimeout(mount, 800)
    }
    if (document.readyState === "complete") schedule()
    else window.addEventListener("load", schedule, { once: true })
    return () => {
      window.removeEventListener("load", schedule)
      if (idleId !== null && typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(idleId)
      if (timeoutId !== null) window.clearTimeout(timeoutId)
    }
  }, [videoCount])
  return ready ? <ProductVideoSlider section={section} /> : null
}
